import { Component, computed, effect, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { ApiService } from "../core/api.service";
import { AuthService } from "../core/auth.service";
import { LimitService } from "../core/limit.service";
import { BillingStatus, PlanConfig, TokenBundle } from "../core/models";

/** Maps a blocked resource key to the bundle grant field that unblocks it. */
const GRANT_FIELD: Record<string, keyof TokenBundle> = {
  projects: "grant_projects",
  clients: "grant_clients",
  employees: "grant_employees",
  subcontractors: "grant_subcontractors",
  proposals: "grant_proposals",
  images: "grant_images",
  ad_images: "grant_ad_images",
  ad_videos: "grant_ad_videos",
};

const RESOURCE_LABELS: Record<string, string> = {
  projects: "projects",
  clients: "clients",
  employees: "team members",
  subcontractors: "subcontractors",
  proposals: "site proposals",
  images: "project images",
  ad_images: "AI ad images",
  ad_videos: "AI ad videos",
};

@Component({
  selector: "app-limit-dialog",
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="limit-backdrop" *ngIf="limits.event() as ev" (click)="dismiss()">
      <div class="limit-dialog card" (click)="$event.stopPropagation()">
        <div class="limit-icon" [class.upgrade]="ev.code === 'upgrade_required'">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
        </div>

        <div class="limit-title">
          {{ ev.code === "upgrade_required" ? "Upgrade to unlock this" : "You've hit a plan limit" }}
        </div>
        <div class="limit-msg">{{ ev.message || defaultMessage(ev) }}</div>

        <ng-container *ngIf="auth.isAdmin(); else notAdmin">
          <div *ngIf="loading()" class="spinner" style="margin:16px auto"></div>

          <ng-container *ngIf="!loading()">
            <!-- Recommended one-click bundle -->
            <div class="reco card" *ngIf="recommended() as b">
              <div class="reco-head">
                <span class="reco-badge">Recommended</span>
                <span class="reco-name">{{ b.name }}</span>
              </div>
              <div class="reco-grant" *ngIf="grantLine(b) as g">{{ g }}</div>
              <button class="btn btn-primary" style="width:100%" [disabled]="busy()" (click)="buy(b)">
                {{ busy() ? "Redirecting to checkout…" : "Buy now — " + money(b.price_cents, b.currency) }}
              </button>
              <div class="reco-note">One-time purchase · Secure Stripe checkout · You'll return right here</div>
            </div>

            <!-- Recommended one-click plan upgrade -->
            <div class="reco card" *ngIf="recommendedPlan() as p">
              <div class="reco-head">
                <span class="reco-badge">Recommended</span>
                <span class="reco-name">{{ p.name }} plan</span>
              </div>
              <div class="reco-grant">Includes the Marketing Hub and higher plan limits.</div>
              <button class="btn btn-primary" style="width:100%" [disabled]="busy()" (click)="upgrade(p)">
                {{ busy() ? "Working…" : "Upgrade now — " + money(p.price_cents, p.currency) + "/mo" }}
              </button>
              <div class="reco-note">Prorated automatically on active subscriptions · Secure Stripe checkout</div>
            </div>

            <div class="limit-alt">
              <a routerLink="/billing" [queryParams]="{ tab: 'plans' }" (click)="dismiss()">
                {{ recommended() || recommendedPlan() ? "Compare all plans →" : "See plans & upgrades →" }}
              </a>
              <a routerLink="/billing" [queryParams]="{ tab: 'bundles' }" (click)="dismiss()" *ngIf="recommended()">
                View all token bundles
              </a>
            </div>

            <div class="limit-msg" style="color:#b45309" *ngIf="error()">{{ error() }}</div>
          </ng-container>
        </ng-container>

        <ng-template #notAdmin>
          <div class="limit-msg" style="margin-top:8px">
            Ask a workspace admin to upgrade the plan or buy a token bundle to continue.
          </div>
        </ng-template>

        <button class="btn limit-dismiss" (click)="dismiss()">Not now</button>
      </div>
    </div>
  `,
  styles: [
    `
      .limit-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 16px;
      }
      .limit-dialog {
        width: 100%;
        max-width: 420px;
        padding: 24px;
        text-align: center;
        background: var(--surface, #fff);
        border-radius: 14px;
        box-shadow: 0 20px 50px rgba(2, 6, 23, 0.25);
      }
      .limit-icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        margin: 0 auto 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fef3c7;
        color: #b45309;
      }
      .limit-icon.upgrade {
        background: #ede9fe;
        color: #6d28d9;
      }
      .limit-icon svg {
        width: 22px;
        height: 22px;
      }
      .limit-title {
        font-weight: 800;
        font-size: 17px;
        margin-bottom: 6px;
      }
      .limit-msg {
        font-size: 13px;
        color: var(--text-2);
        line-height: 1.5;
      }
      .reco {
        margin: 16px 0 10px;
        padding: 14px;
        border: 1px solid #bfdbfe;
        background: #eff6ff;
        border-radius: 12px;
        text-align: left;
      }
      .reco-head {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }
      .reco-badge {
        font-size: 10.5px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        background: #1d4ed8;
        color: #fff;
        border-radius: 999px;
        padding: 2px 8px;
      }
      .reco-name {
        font-weight: 700;
        font-size: 14px;
      }
      .reco-grant {
        font-size: 12.5px;
        color: var(--text-2);
        margin-bottom: 10px;
      }
      .reco-note {
        font-size: 11px;
        color: var(--text-3, #94a3b8);
        margin-top: 8px;
        text-align: center;
      }
      .limit-alt {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 6px;
      }
      .limit-alt a {
        font-size: 12.5px;
        color: var(--brand-strong, #1d4ed8);
        text-decoration: none;
        font-weight: 600;
      }
      .limit-dismiss {
        margin-top: 14px;
        width: 100%;
      }
    `,
  ],
})
export class LimitDialogComponent {
  limits = inject(LimitService);
  auth = inject(AuthService);
  private api = inject(ApiService);
  private router = inject(Router);

  loading = signal(false);
  busy = signal(false);
  error = signal<string | null>(null);
  private bundles = signal<TokenBundle[]>([]);
  private plans = signal<PlanConfig[]>([]);
  private billingStatus = signal<BillingStatus | null>(null);
  private loadedFor: string | null = null;

  recommended = computed<TokenBundle | null>(() => {
    const ev = this.limits.event();
    if (!ev || ev.code !== "limit_reached" || !ev.resource) return null;
    const field = GRANT_FIELD[ev.resource];
    if (!field) return null;
    const candidates = this.bundles()
      .filter((b) => ((b[field] as number) || 0) > 0 && b.price_cents > 0)
      .sort((a, b) => a.price_cents - b.price_cents);
    return candidates[0] || null;
  });

  /** Cheapest plan that unlocks the gated feature (upgrade_required flow). */
  recommendedPlan = computed<PlanConfig | null>(() => {
    const ev = this.limits.event();
    if (!ev || ev.code !== "upgrade_required") return null;
    const s = this.billingStatus();
    const candidates = this.plans()
      .filter((p) => p.marketing_hub && (p.price_cents || 0) > 0)
      .filter((p) => !s || !s.has_subscription || p.tier !== s.plan_tier)
      .sort((a, b) => (a.price_cents || 0) - (b.price_cents || 0));
    return candidates[0] || null;
  });

  constructor() {
    // Load purchase options once per dialog open (admins only — the billing
    // endpoints are admin-gated, so non-admins just get guidance text).
    effect(() => {
      const ev = this.limits.event();
      if (!ev) {
        // Reset the load key on close so a transient load failure can be
        // retried the next time the same limit event fires.
        this.loadedFor = null;
        return;
      }
      if (!this.auth.isAdmin()) return;
      const key = `${ev.code}:${ev.resource || ev.feature || ""}`;
      if (this.loadedFor === key) return;
      this.loadedFor = key;
      this.error.set(null);
      this.busy.set(false);
      this.loading.set(true);
      if (ev.code === "limit_reached") {
        this.api.billingBundles().subscribe({
          next: (b) => {
            this.bundles.set(b);
            this.loading.set(false);
          },
          error: () => {
            this.loadedFor = null;
            this.loading.set(false);
            this.error.set("Could not load token bundles. Visit the Billing page instead.");
          },
        });
      } else {
        this.api.billingPlans().subscribe({
          next: (p) => {
            this.plans.set(p);
            this.loading.set(false);
          },
          error: () => {
            this.loadedFor = null;
            this.loading.set(false);
            this.error.set("Could not load plans. Visit the Billing page instead.");
          },
        });
        this.api.getBilling().subscribe({
          next: (s) => this.billingStatus.set(s),
          error: () => {},
        });
      }
    });
  }

  upgrade(p: PlanConfig) {
    this.busy.set(true);
    this.error.set(null);
    const s = this.billingStatus();
    const origin = window.location.origin;
    const done = (r: { url?: string | null }) => {
      if (r.url) {
        window.location.href = r.url;
      } else {
        // Prorated in-place upgrade (no checkout hop) — refresh and close.
        this.busy.set(false);
        this.limits.close();
        this.auth.refresh().subscribe({ next: () => {}, error: () => {} });
      }
    };
    const fail = (e: any) => {
      this.busy.set(false);
      this.error.set(
        typeof e?.error?.detail === "string" ? e.error.detail : "Could not start checkout.",
      );
    };
    if (s?.has_subscription) {
      this.api.billingUpgrade(origin, p.tier).subscribe({ next: done, error: fail });
    } else {
      this.api.billingCheckout(origin, p.tier).subscribe({ next: done, error: fail });
    }
  }

  buy(b: TokenBundle) {
    this.busy.set(true);
    this.error.set(null);
    const returnTo = this.router.url.split("?")[0] || "/";
    this.api.billingBundleCheckout(b.id, window.location.origin, returnTo).subscribe({
      next: (r) => (window.location.href = r.url),
      error: (e) => {
        this.busy.set(false);
        this.error.set(
          typeof e?.error?.detail === "string" ? e.error.detail : "Could not start checkout.",
        );
      },
    });
  }

  grantLine(b: TokenBundle): string {
    const parts: string[] = [];
    const push = (qty: number | null | undefined, label: string) => {
      if (qty) parts.push(`+${qty} ${label}`);
    };
    push(b.grant_projects, "projects");
    push(b.grant_clients, "clients");
    push(b.grant_employees, "team members");
    push(b.grant_subcontractors, "subcontractors");
    push(b.grant_proposals, "proposals");
    push(b.grant_images, "images");
    push(b.grant_ad_images, "AI ad images");
    push(b.grant_ad_videos, "AI ad videos");
    return parts.join(" · ");
  }

  defaultMessage(ev: { resource?: string; feature?: string }): string {
    if (ev.feature === "marketing")
      return "The Marketing Hub isn't included in your current plan.";
    const label = RESOURCE_LABELS[ev.resource || ""] || "this resource";
    return `You've used all of your plan's allowance for ${label}.`;
  }

  money(cents: number | null | undefined, currency: string | null | undefined): string {
    if (cents == null) return "—";
    const amount = cents / 100;
    const cur = (currency || "usd").toUpperCase();
    const symbol = cur === "USD" || cur === "CAD" || cur === "AUD" ? "$" : "";
    const formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    return symbol ? `${symbol}${formatted}` : `${formatted} ${cur}`;
  }

  dismiss() {
    this.limits.close();
  }
}
